import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import GuestTable from "@/app/admin/_common/guestlist/GuestTable";
import { Input } from "@/app/admin/_common/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { Show } from "@/typings";
import { useCan } from "@/app/_utils/sessionClient";
import ShowService from "@/app/_services/ShowService";
import OrderDrawer from "./_cmp/forms/order/OrderDrawer";
import { debounce, filter } from "lodash";
import { PlusCircle, Download, MoreVertical, Search } from "lucide-react";
import { PERMISSIONS } from "@/app/_utils/permissions";

export default function ShowGuests() {
  const { show_id } = useParams<{ show_id: string }>();
  const [show, setShow] = useState<Show>();
  const [isLoadingGuests, setIsLoadingGuests] = useState(true);
  const [guestList, setGuestList] = useState<Array<any>>([]);
  const [cachedGuestList, setCachedGuestList] = useState<Array<any>>([]);
  const [checkInCount, setCheckInCount] = useState(0);
  const [checkInTotal, setCheckInTotal] = useState(0);
  const [futureShows, setFutureShows] = useState<Array<any>>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [compedOnly, setCompedOnly] = useState(false);
  const isAdmin = useCan(PERMISSIONS.GUEST_WRITE);

  async function getGuests() {
    if (!show_id) return;
    setIsLoadingGuests(true);
    try {
      const { guests, count, total } = await ShowService.getGuestList({ show_id });
      setCheckInCount(count);
      setCheckInTotal(total);
      setGuestList(guests);
      setCachedGuestList(guests);
    } finally {
      setIsLoadingGuests(false);
    }
  }

  async function getShow() {
    if (!show_id) return;
    const res = await ShowService.getShow({ id: show_id });
    setShow(res);
  }

  async function getFutureShows() {
    const res = await ShowService.getShow({ future: true });
    setFutureShows(Array.isArray(res) ? res : []);
  }

  useEffect(() => {
    if (!show_id) return;
    getShow();
    getGuests();
    getFutureShows();
  }, [show_id]);

  const exportGuestList = async () => {
    if (!show_id) return;
    const res = await ShowService.exportGuestList(show_id);
    const url = window.URL.createObjectURL(res);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${show?.title ? (show?.variant_name ? show?.title + "-" + show?.variant_name : show?.title) : "guestlist"}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleSearch = debounce((value: string) => {
    setGuestList(
      filter(cachedGuestList, (order) => {
        return (
          order?.guest_data?.first_name?.toLowerCase().includes(value.toLowerCase()) ||
          order?.guest_data?.last_name?.toLowerCase().includes(value.toLowerCase())
        );
      })
    );
  }, 300);

  function handleCompedOnly(next: boolean) {
    setCompedOnly(next);
    setGuestList(next ? filter(cachedGuestList, (order) => order?.comped === true) : cachedGuestList);
  }

  const checkInPct = checkInTotal ? Math.min(100, (checkInCount / checkInTotal) * 100) : 0;

  return (
    <div className="flex h-[calc(100%-10px)] flex-col gap-3">
      <div className="flex flex-col gap-3">
        <div className="w-full">
          <div className="mb-1.5 flex items-center justify-between text-[12.5px]">
            <span className="text-muted-foreground">Checked in</span>
            <span className="text-foreground">{checkInCount} / {checkInTotal} guests</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-foreground" style={{ width: `${checkInPct}%` }} />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-[220px] md:flex-none">
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <Search className="size-3.5 text-muted-foreground" />
            </div>
            <Input
              type="search"
              placeholder="Search guests…"
              onChange={(e) => handleSearch(e.target.value)}
              className="h-[30px] pl-9 text-[12.5px]"
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Guest list actions"
                className="inline-flex size-[30px] flex-none items-center justify-center rounded-md border border-input bg-card text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                <MoreVertical className="size-4 shrink-0" size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {isAdmin && (
                <DropdownMenuItem onSelect={() => setDrawerOpen(true)}>
                  <PlusCircle className="size-3.5" />
                  Add guest
                </DropdownMenuItem>
              )}
              <DropdownMenuCheckboxItem
                checked={compedOnly}
                onCheckedChange={(checked) => handleCompedOnly(Boolean(checked))}
              >
                Comps Only
              </DropdownMenuCheckboxItem>
              <DropdownMenuItem onSelect={exportGuestList}>
                <Download className="size-3.5" />
                Export
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="-mx-3 flex min-h-0 flex-1 flex-col md:mx-0">
        <GuestTable
          isLoading={isLoadingGuests}
          setCheckInCount={setCheckInCount}
          guests={guestList}
          containerClassName="flex min-h-0 flex-1 w-full flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border"
          cachedGuests={cachedGuestList}
          setCachedGuests={setCachedGuestList}
          show={show}
          showId={show_id}
          futureShows={futureShows}
          onChanged={getGuests}
        />
      </div>

      <OrderDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} show={show} />
    </div>
  );
}
